import { SEO_DEFAULTS } from '../../utils/seoConfig';
import { isoDuration } from './podcastShow';

/**
 * podcastSchema — JSON-LD for /podcast, per Rhea's S2 spec (CEL-1355 PRD;
 * CEL-1362: show/episodes now come from the RUNTIME registry, so every
 * generator takes them as arguments instead of importing a static module):
 *  - Episode page: @graph of WebPage → PodcastEpisode → PodcastSeries +
 *    AudioObject, with stable @ids and the org @id
 *    (https://cellcog.ai/#organization) as publisher. Plus BreadcrumbList.
 *  - Hub: PodcastSeries + CollectionPage/ItemList of LIVE episodes only.
 *  - Deliberate REFUSALS (S2): NO transcript text in JSON-LD (visible HTML
 *    only — bloat + dupe-drift risk, zero feature gain); Person entity for
 *    Rhea (an AI employee as schema Person/host — deliberate, on-brand, and
 *    factually the show's host).
 * The SSR pages (py/cellcog/api/podcast_pages.py) render byte-equivalent
 * nodes server-side for crawlers; on hydration these replace them (createRoot
 * swaps #root wholesale — never two copies).
 */

const SERIES_ID = `${SEO_DEFAULTS.url}/podcast/#series`;
const HOST_ID = `${SEO_DEFAULTS.url}/podcast/#host-rhea`;
const ORG_ID = `${SEO_DEFAULTS.url}/#organization`;

const seriesNode = (show) => ({
  '@type': 'PodcastSeries',
  '@id': SERIES_ID,
  name: show.name,
  description: show.description,
  url: `${SEO_DEFAULTS.url}/podcast/`,
  webFeed: show.feedUrl,
  image: `${SEO_DEFAULTS.url}${show.artwork}`,
  author: { '@id': HOST_ID },
  publisher: { '@id': ORG_ID },
});

const hostNode = (show) => ({
  '@type': 'Person',
  '@id': HOST_ID,
  name: show.host.name,
  jobTitle: show.host.role,
  sameAs: [`${SEO_DEFAULTS.url}/ai-organization`],
});

/** Guest Person node — guest's external site as url/sameAs (SEO give-back;
 * mirrors _guest_node in py/cellcog/api/podcast_pages.py — keep in sync). */
const guestNode = (e, pageUrl) => {
  const node = {
    '@type': 'Person',
    '@id': `${pageUrl}#guest`,
    name: e.guest.name,
    jobTitle: e.guest.role,
  };
  const link = (e.guest.link || '').trim();
  if (link.startsWith('http')) {
    node.url = link;
    node.sameAs = [link];
  }
  return node;
};

export const generatePodcastEpisodeSchema = (e, show) => {
  const pageUrl = `${SEO_DEFAULTS.url}/podcast/${e.slug}/`;
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': pageUrl,
        url: pageUrl,
        name: e.seoTitle || e.title,
        description: e.description,
        mainEntity: { '@id': `${pageUrl}#episode` },
      },
      {
        '@type': 'PodcastEpisode',
        '@id': `${pageUrl}#episode`,
        name: e.title,
        description: e.description,
        url: pageUrl,
        episodeNumber: e.episodeNumber,
        ...(e.season ? { partOfSeason: { '@type': 'PodcastSeason', seasonNumber: e.season } } : {}),
        ...(e.datePublished ? { datePublished: e.datePublished } : {}),
        duration: isoDuration(e.durationSeconds),
        image: `${SEO_DEFAULTS.url}${e.artwork}`,
        partOfSeries: { '@id': SERIES_ID },
        author: { '@id': HOST_ID },
        publisher: { '@id': ORG_ID },
        associatedMedia: {
          '@type': 'AudioObject',
          contentUrl: e.audioUrl,
          encodingFormat: 'audio/mpeg',
          duration: isoDuration(e.durationSeconds),
        },
        inLanguage: 'en',
        isFamilyFriendly: true,
        ...(e.guest ? { actor: { '@id': `${pageUrl}#guest` } } : {}),
      },
      seriesNode(show),
      hostNode(show),
      ...(e.guest ? [guestNode(e, pageUrl)] : []),
    ],
  };
};

export const generatePodcastBreadcrumbSchema = (e) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Podcast',
      item: `${SEO_DEFAULTS.url}/podcast/`,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: e.title,
      item: `${SEO_DEFAULTS.url}/podcast/${e.slug}/`,
    },
  ],
});

/** Hub: PodcastSeries + CollectionPage/ItemList — LIVE entries only (D4). */
export const generatePodcastHubSchema = (liveEpisodesSorted, show) => ({
  '@context': 'https://schema.org',
  '@graph': [
    seriesNode(show),
    hostNode(show),
    {
      '@type': 'CollectionPage',
      name: show.name,
      description: show.description,
      url: `${SEO_DEFAULTS.url}/podcast/`,
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: liveEpisodesSorted.map((e, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: e.title,
          url: `${SEO_DEFAULTS.url}/podcast/${e.slug}/`,
        })),
      },
    },
  ],
});
